/**
 * @file lib/auth/gftd-auth-client.ts
 * @description GFTD認証システムクライアント
 * @author GFTD Co., Ltd.
 */

import { GFTDAuthConfig } from '../types/config'

/**
 * 認証トークン情報
 */
export interface AuthToken {
  /** アクセストークン */
  accessToken: string
  /** リフレッシュトークン */
  refreshToken?: string
  /** 有効期限 (Unix timestamp) */
  expiresAt: number
  /** トークンタイプ */
  tokenType: string
  /** スコープ */
  scope: string[]
}

/**
 * ユーザー情報
 */
export interface UserInfo {
  /** ユーザーID */
  id: string
  /** ユーザー名 */
  username: string
  /** メールアドレス */
  email: string
  /** ロール */
  role: string[]
  /** GitLab組織ID (user_org_id = gitlab organization_id) */
  user_org_id?: string
  /** 認証済みかどうか */
  authenticated: boolean
}

/**
 * 認証エラー
 */
export class GFTDAuthError extends Error {
  constructor(
    message: string,
    public statusCode: number = 401,
    public code: string = 'AUTH_ERROR'
  ) {
    super(message)
    this.name = 'GFTDAuthError'
  }
}

/**
 * GFTD認証クライアント
 */
export class GFTDAuthClient {
  private config: GFTDAuthConfig
  private currentToken?: AuthToken
  private userInfo?: UserInfo

  constructor(config: GFTDAuthConfig) {
    this.config = config
  }

  /**
   * 認証が必須かどうか
   */
  isAuthRequired(): boolean {
    return this.config.required !== false
  }

  /**
   * 認証済みかどうか
   */
  isAuthenticated(): boolean {
    return !!this.currentToken && this.isTokenValid(this.currentToken)
  }

  /**
   * トークンが有効かどうか
   */
  private isTokenValid(token: AuthToken): boolean {
    return Date.now() < token.expiresAt * 1000
  }

  /**
   * 現在のユーザー情報を取得
   */
  getCurrentUser(): UserInfo | null {
    return this.userInfo || null
  }

  /**
   * 認証トークンを設定
   */
  setToken(token: AuthToken): void {
    this.currentToken = token
  }

  /**
   * 認証トークンを取得
   */
  getToken(): AuthToken | null {
    return this.currentToken || null
  }

  /**
   * ログインURLを生成
   */
  generateLoginUrl(redirectUrl?: string): string {
    const authUrl = this.config.authUrl || 'https://auth.gftd.ai'
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.config.apiKey || '',
      redirect_uri: redirectUrl || this.config.redirectUrl || '',
      scope: this.config.scopes?.join(' ') || 'read write',
      state: this.generateState()
    })

    return `${authUrl}/oauth/authorize?${params.toString()}`
  }

  /**
   * 認証コードからトークンを取得
   */
  async exchangeCodeForToken(code: string, redirectUri?: string): Promise<AuthToken> {
    if (!this.config.authUrl) {
      throw new GFTDAuthError('Auth URL not configured')
    }

    try {
      const response = await fetch(`${this.config.authUrl}/oauth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          grant_type: 'authorization_code',
          code,
          client_id: this.config.apiKey,
          redirect_uri: redirectUri || this.config.redirectUrl
        })
      })

      if (!response.ok) {
        throw new GFTDAuthError(`Authentication failed: ${response.statusText}`, response.status)
      }

      const tokenData = await response.json()
      const token: AuthToken = {
        accessToken: tokenData.access_token,
        refreshToken: tokenData.refresh_token,
        expiresAt: Date.now() / 1000 + (tokenData.expires_in || this.config.tokenExpiry || 3600),
        tokenType: tokenData.token_type || 'Bearer',
        scope: tokenData.scope ? tokenData.scope.split(' ') : this.config.scopes || []
      }

      this.setToken(token)
      await this.loadUserInfo()

      return token
    } catch (error) {
      if (error instanceof GFTDAuthError) {
        throw error
      }
      throw new GFTDAuthError(`Token exchange failed: ${error}`)
    }
  }

  /**
   * トークンを更新
   */
  async refreshToken(): Promise<AuthToken> {
    if (!this.currentToken?.refreshToken) {
      throw new GFTDAuthError('No refresh token available')
    }

    if (!this.config.authUrl) {
      throw new GFTDAuthError('Auth URL not configured')
    }

    try {
      const response = await fetch(`${this.config.authUrl}/oauth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          grant_type: 'refresh_token',
          refresh_token: this.currentToken.refreshToken,
          client_id: this.config.apiKey
        })
      })

      if (!response.ok) {
        throw new GFTDAuthError(`Token refresh failed: ${response.statusText}`, response.status)
      }

      const tokenData = await response.json()
      const newToken: AuthToken = {
        accessToken: tokenData.access_token,
        refreshToken: tokenData.refresh_token || this.currentToken.refreshToken,
        expiresAt: Date.now() / 1000 + (tokenData.expires_in || this.config.tokenExpiry || 3600),
        tokenType: tokenData.token_type || 'Bearer',
        scope: tokenData.scope ? tokenData.scope.split(' ') : this.currentToken.scope
      }

      this.setToken(newToken)
      return newToken
    } catch (error) {
      if (error instanceof GFTDAuthError) {
        throw error
      }
      throw new GFTDAuthError(`Token refresh failed: ${error}`)
    }
  }

  /**
   * ユーザー情報を読み込み
   */
  async loadUserInfo(): Promise<UserInfo> {
    if (!this.currentToken) {
      throw new GFTDAuthError('No authentication token available')
    }

    if (!this.config.authUrl) {
      throw new GFTDAuthError('Auth URL not configured')
    }

    try {
      const response = await fetch(`${this.config.authUrl}/api/user`, {
        headers: {
          'Authorization': `${this.currentToken.tokenType} ${this.currentToken.accessToken}`,
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        throw new GFTDAuthError(`Failed to load user info: ${response.statusText}`, response.status)
      }

      const userData = await response.json()
      this.userInfo = {
        id: userData.id,
        username: userData.username,
        email: userData.email,
        role: userData.roles || [],
        user_org_id: userData.organization_id,
        authenticated: true
      }

      return this.userInfo
    } catch (error) {
      if (error instanceof GFTDAuthError) {
        throw error
      }
      throw new GFTDAuthError(`Failed to load user info: ${error}`)
    }
  }

  /**
   * ログアウト
   */
  async logout(): Promise<void> {
    if (this.currentToken && this.config.authUrl) {
      try {
        await fetch(`${this.config.authUrl}/oauth/revoke`, {
          method: 'POST',
          headers: {
            'Authorization': `${this.currentToken.tokenType} ${this.currentToken.accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            token: this.currentToken.accessToken
          })
        })
      } catch (error) {
        console.warn('Failed to revoke token:', error)
      }
    }

    this.currentToken = undefined
    this.userInfo = undefined
  }

  /**
   * 認証が必要な場合のチェック
   */
  async ensureAuthenticated(): Promise<void> {
    if (!this.isAuthRequired()) {
      return
    }

    if (!this.isAuthenticated()) {
      throw new GFTDAuthError('Authentication required', 401, 'AUTH_REQUIRED')
    }

    // トークンの自動更新
    if (this.config.autoRefresh && this.currentToken && this.needsRefresh()) {
      try {
        await this.refreshToken()
      } catch (error) {
        throw new GFTDAuthError('Failed to refresh authentication token', 401, 'TOKEN_REFRESH_FAILED')
      }
    }
  }

  /**
   * トークンの更新が必要かどうか
   */
  private needsRefresh(): boolean {
    if (!this.currentToken) return false
    // 有効期限の5分前に更新
    const refreshThreshold = this.currentToken.expiresAt - 300
    return Date.now() / 1000 > refreshThreshold
  }

  /**
   * 状態文字列を生成
   */
  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15)
  }

  /**
   * Authorizationヘッダーを取得
   */
  getAuthHeader(): string | null {
    if (!this.currentToken) return null
    return `${this.currentToken.tokenType} ${this.currentToken.accessToken}`
  }
} 